import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { loadMapApi } from "../gMap/GoogleMapsUtils";
import { getRouteByIdData, getHeartBeatsByIdData } from "../../redux/Thunk/RoutesThunk";
import { GlobalConstants } from "../../../utils/GlobalConstants";
import { Constants } from "../utils/Constants";
import moment from "moment";
import {
  toLiftStatusLabel,
  isSpareStatus,
  LIFT_STATUS_LABEL,
} from "../utils/liftStatus";
import {
  heartbeatIndicator,
  INDICATOR_COLOR,
  INDICATOR_STATE,
  STALE_DAYS,
} from "../utils/liftIndicators";
import {
  getDcLocation,
  countLiftsAtDc,
  buildDcMarkerIcon,
  buildDcIconUrl,
  buildDcInfoWindowContent,
  DC_GEOFENCE_STYLE,
} from "./dcLocation";

// Stacking: geofence circle, then DC icon, then lift pins on top, so a pin
// sitting on the DC stays clickable.
const DC_CIRCLE_Z_INDEX = 0;
const DC_MARKER_Z_INDEX = 1;
const LIFT_MARKER_Z_INDEX = 10;

// Marker colors
const STATUS_COLORS = {
  IN_SERVICE: INDICATOR_COLOR.GREEN,
  INACTIVE: INDICATOR_COLOR.RED, // no valid location for more than STALE_DAYS
  SPARE: INDICATOR_COLOR.GREY, // lift marked Spare (API: "Out of Service")
};

/**
 * Pin colour for a lift: green in service, red inactive, grey Spare.
 *
 * Delegates to heartbeatIndicator so the pin and the Heartbeat Tracking dot
 * on the Lift Details grid are driven by one rule and cannot drift apart.
 * Both age on LAST_VALID_LOCATION_DATE with the STALE_DAYS threshold.
 *
 * Note this no longer uses lift.isStaleHeartbeat. That flag is computed from
 * STALE_HEARTBEAT_HOURS (24h), which also controls route clearing in
 * getLiftsDetailList, so reusing it here would have tied the pin colour to
 * route-clearing behaviour. A lift with no location on record keeps the
 * in-service colour; in practice it has no coordinates so it is not plotted.
 */
export function markerColorFor(lift) {
  const state = heartbeatIndicator(lift).state;
  if (state === INDICATOR_STATE.INACTIVE) return STATUS_COLORS.SPARE;
  if (state === INDICATOR_STATE.CRITICAL) return STATUS_COLORS.INACTIVE;
  return STATUS_COLORS.IN_SERVICE;
}

/**
 * Color for the Status text inside the InfoWindow: grey Spare, green otherwise.
 *
 * Only reads the status, not the heartbeat age, so it is not markerColorFor.
 * An In Service lift that has not reported for over 7 days gets a red pin, but
 * the Status row still reads "In Service" and coloring that text red would
 * contradict the word next to it. The pin carries the staleness; the Status
 * text carries the status.
 */
export function statusTextColorFor(lift) {
  return isSpareStatus(lift?.status) ? STATUS_COLORS.SPARE : STATUS_COLORS.IN_SERVICE;
}

// Data attributes used to wire up the route-link click inside InfoWindow HTML
// (Google Maps strips onclick= so we use domready + querySelector)
const ROUTE_LINK_ATTR = "data-lift-map-route-id";
const ROUTE_NAME_ATTR = "data-lift-map-route-name";

// ── Pure helpers (defined outside the component so they're stable references) ─

/**
 * Builds a colored SVG teardrop-pin icon for a Google Maps Marker.
 * Using an inline data URL guarantees the fill color is applied —
 * google.maps.SymbolPath icons can silently ignore fillColor in some builds.
 */
function buildMarkerIcon(color) {
  const svg = [
    '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="36" viewBox="0 0 24 36">',
    '<path d="M12 0C5.373 0 0 5.373 0 12c0 9 12 24 12 24s12-15 12-24C24 5.373 18.627 0 12 0z"',
    ' fill="' + color + '" stroke="#ffffff" stroke-width="1.5"/>',
    '<circle cx="12" cy="12" r="5" fill="#ffffff" opacity="0.9"/>',
    "</svg>",
  ].join("");

  return {
    url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
    scaledSize: { width: 24, height: 36 },
    anchor: { x: 12, y: 36 },
  };
}

/**
 * Returns the HTML string for the Location row in the InfoWindow.
 * - DC geofence  → plain DC name
 * - Known route  → "On Route (103)" as a clickable <a> with data-* attrs
 * - Unknown      → "On Route" plain text
 */
export function buildLocationHtml(lift) {
  if (lift.inDcGeofence && lift.dc) {
    return lift.dc;
  }
  const hasRoute =
    lift.routeId != null && lift.routeName && lift.routeName !== "On Route";
  if (hasRoute) {
    return (
      '<a href="#"' +
      ' style="color:#2027A5;text-decoration:underline;cursor:pointer;"' +
      ' ' + ROUTE_LINK_ATTR + '="' + lift.routeId + '"' +
      ' ' + ROUTE_NAME_ATTR + '="' + lift.routeName + '"' +
      ">On Route (" + lift.routeName + ")</a>"
    );
  }

  // Inactive or Spare: the backend has cleared the active route, so fall back
  // to the route the lift last ran. Dated, because for a long-silent lift the
  // route number alone invites the reader to assume it is current.
  if (lift.lastRouteName) {
    const dateSuffix = lift.lastRouteDate
      ? " on " + moment(lift.lastRouteDate).format("MMM D, YYYY")
      : "";
    const label = "Last on Route " + lift.lastRouteName + dateSuffix;
    if (lift.lastRouteId != null) {
      return (
        '<a href="#"' +
        ' style="color:#2027A5;text-decoration:underline;cursor:pointer;"' +
        ' ' + ROUTE_LINK_ATTR + '="' + lift.lastRouteId + '"' +
        ' ' + ROUTE_NAME_ATTR + '="' + lift.lastRouteName + '"' +
        ">" + label + "</a>"
      );
    }
    return label;
  }

  return lift.routeName || "On Route";
}

/**
 * Builds the full InfoWindow HTML for one lift.
 * Driver row is omitted entirely when driverName is null/empty.
 */
export function buildInfoWindowContent(lift) {
  // Not liftStatusColor: that colors Spare red for the grids. On the map red
  // means "no valid location in over 7 days", so Spare takes the same grey as
  // its pin and the legend swatch. QA raised the grey pin / red text mismatch.
  const statusColor = statusTextColorFor(lift);
  const statusLabel = toLiftStatusLabel(lift.status);

  // Last Data Update — advances on every heartbeat (Location.qo or RFID.qo),
  // even when no GPS fix is included.  Formerly labelled "Last Reported".
  const lastDataUpdate = lift.lastReportedTime
    ? moment(lift.lastReportedTime).format("MMM D, YYYY hh:mm A")
    : "n/a";

  // Last Valid Location — only advances when the heartbeat carried a real
  // GPS fix (non-zero lat/lon).  Stays on the last good GPS timestamp while
  // the lift sends RF/RFID-only events from an unknown or unchanged location.
  const lastValidLocation = lift.lastValidLocationDate
    ? moment(lift.lastValidLocationDate).format("MMM D, YYYY hh:mm A")
    : "No Valid GPS recorded";

  const battery =
    lift.batteryPercentage !== null && lift.batteryPercentage !== undefined
      ? lift.batteryPercentage + "%"
      : "n/a";

  const aliasRow = lift.customerLiftAlias
    ? "<div><b>Alias:</b> " + lift.customerLiftAlias + "</div>"
    : "";

  // Driver is always shown, rather than omitted when absent, so a red or grey
  // pin does not silently lose the row.
  //
  // For an inactive or Spare lift the backend returns driverName as null,
  // because getLiftsDetailList clears the active route once a lift passes
  // STALE_HEARTBEAT_HOURS. lastDriverName / lastRouteName are the
  // last-heartbeat values and are preferred when the live ones are gone; they
  // are not in the payload yet, so until the API supplies them this falls back
  // to "Unassigned". See buildLocationHtml for the same treatment of the route.
  const driverName = lift.driverName || lift.lastDriverName || "Unassigned";
  const driverRow = "<div><b>Driver:</b> " + driverName + "</div>";

  return (
    '<div style="font-family:Arial,sans-serif;font-size:13px;min-width:220px;line-height:1.8;">' +
    '<div style="font-weight:bold;font-size:15px;color:#2027A5;margin-bottom:6px;">' +
    (lift.liftName || lift.liftId) +
    "</div>" +
    aliasRow +
    '<div><b>Status:</b> <span style="color:' + statusColor + ';">' + statusLabel + "</span></div>" +
    "<div><b>Location:</b> " + buildLocationHtml(lift) + "</div>" +
    driverRow +
    "<div><b>Last Valid Location:</b> " + lastValidLocation + "</div>" +
    "<div><b>Last Updated:</b> " + lastDataUpdate + "</div>" +
    "<div><b>Battery:</b> " + battery + "</div>" +
    "</div>"
  );
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * LiftMapView
 *
 * Renders a Google Map with one marker per lift that has valid coordinates.
 * Green marker = In Service, Red marker = no valid location for more than
 * STALE_DAYS, Grey marker = Spare.
 * Clicking a marker opens an InfoWindow with lift details; the route number
 * (when known) is a hyperlink that navigates to the Route Details page.
 *
 * The selected DC is drawn as a blue square warehouse icon and is included in the
 * initial map bounds, so it is visible on default load.
 *
 * Props:
 *   liftList    – array of lift objects from Redux (filteredLiftList)
 *   loading     – boolean, shows a spinner while the lift data is being fetched
 *   dcLocation  – selected DC location object from /v2/admin/locations
 *                 (locationName, locationLatitude, locationLongitude). Optional.
 */
export default function LiftMapView({ liftList = [], loading = false, dcLocation = null }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const dcMarkerRef = useRef(null);
  const dcCircleRef = useRef(null);
  const infoWindowRef = useRef(null);

  const dc = getDcLocation(dcLocation);
  // Primitive deps so the marker effect only re-runs when the DC actually changes.
  const dcKey = dc ? [dc.name, dc.latitude, dc.longitude, dc.radiusMeters].join("|") : "";

  // mapReady     → Google Maps script has loaded
  // mapInitialised → the Map instance has been created (triggers marker effect)
  const [mapReady, setMapReady] = useState(false);
  const [mapInitialised, setMapInitialised] = useState(false);
  const [noCoords, setNoCoords] = useState(false);

  // ── 1. Load the Google Maps JS API script ───────────────────────────────────
  useEffect(() => {
    if (window.google && window.google.maps) {
      setMapReady(true);
      return;
    }
    const script = loadMapApi();
    if (!script) return;
    const onLoad = () => setMapReady(true);
    script.addEventListener("load", onLoad);
    return () => script.removeEventListener("load", onLoad);
  }, []);

  // ── 2. Initialise the Map instance once the script is ready ────────────────
  useEffect(() => {
    if (!mapReady || !mapContainerRef.current) return;
    if (mapInstanceRef.current) return; // already done

    const google = window.google;

    mapInstanceRef.current = new google.maps.Map(mapContainerRef.current, {
      zoom: 10,
      center: { lat: 39.8283, lng: -98.5795 },
      mapTypeId: "satellite", // default to satellite view per Pepsi request
      gestureHandling: "greedy",
      streetViewControl: true,
      zoomControl: true,
      fullscreenControl: true,
      fullscreenControlOptions: { position: google.maps.ControlPosition.TOP_RIGHT },
      mapTypeControl: true,
      mapTypeControlOptions: { position: google.maps.ControlPosition.BOTTOM_RIGHT },
    });

    infoWindowRef.current = new google.maps.InfoWindow();

    // Wire up the route-link click after the InfoWindow content is in the DOM.
    // Google Maps sanitises InfoWindow HTML and removes onclick= attributes,
    // so we attach the listener imperatively via the domready event.
    google.maps.event.addListener(infoWindowRef.current, "domready", () => {
      const link = document.querySelector("[" + ROUTE_LINK_ATTR + "]");
      if (!link) return;

      const routeId = link.getAttribute(ROUTE_LINK_ATTR);
      const routeName = link.getAttribute(ROUTE_NAME_ATTR);

      link.addEventListener("click", function (e) {
        e.preventDefault();
        infoWindowRef.current.close();

        const routeData = [{ routeId: routeId, routeName: routeName }];
        localStorage.setItem("routeDBRoutes", JSON.stringify(routeData));
        GlobalConstants.IS_ROUTE_DETAILS_LOADING = false;
        GlobalConstants.SELETED_ROUTE_INDEX = 0;
        GlobalConstants.NAVIGATE_ROUTE_DETAILS = true;
        localStorage.setItem("routeIndex", 0);

        dispatch(getRouteByIdData(routeData));
        dispatch(getHeartBeatsByIdData(routeData));

        const evt = new CustomEvent(Constants.ROUTE_SELECTION_EVENT, { detail: {} });
        document.dispatchEvent(evt);
        navigate("/routedetails");
      });
    });

    // Flip the flag so the marker-plotting effect can run even if liftList
    // was already populated before the map finished initialising.
    setMapInitialised(true);
  }, [mapReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 3. Plot / refresh markers whenever the list or the map changes ──────────
  useEffect(() => {
    if (!mapInitialised || !mapInstanceRef.current) return;

    const google = window.google;
    const map = mapInstanceRef.current;

    // Remove previous markers
    markersRef.current.forEach(function (m) { m.setMap(null); });
    markersRef.current = [];
    if (dcMarkerRef.current) {
      dcMarkerRef.current.setMap(null);
      dcMarkerRef.current = null;
    }
    if (dcCircleRef.current) {
      dcCircleRef.current.setMap(null);
      dcCircleRef.current = null;
    }

    const liftsWithCoords = (liftList || []).filter(function (lift) {
      return (
        lift.latitude != null &&
        lift.longitude != null &&
        !isNaN(Number(lift.latitude)) &&
        !isNaN(Number(lift.longitude))
      );
    });

    // With no lift coordinates, still show the map if the DC itself can be plotted.
    if (liftsWithCoords.length === 0 && !dc) {
      setNoCoords(true);
      return;
    }
    setNoCoords(false);

    console.log(
      "[LiftMapView] plotting", liftsWithCoords.length, "lifts —",
      liftsWithCoords.map(function(l) { return l.liftId + ":" + l.status; })
    );

    const bounds = new google.maps.LatLngBounds();

    liftsWithCoords.forEach(function (lift) {
      const position = new google.maps.LatLng(
        Number(lift.latitude),
        Number(lift.longitude)
      );
      bounds.extend(position);

      // isStaleHeartbeat is computed by the backend (UTC comparison before DC-local
      // timezone conversion) using the STALE_HEARTBEAT_HOURS Lambda env var.
      const markerColor = markerColorFor(lift);

      const markerTitle = lift.customerLiftAlias || lift.liftName || lift.liftId;

      const marker = new google.maps.Marker({
        position: position,
        map: map,
        title: markerTitle,
        icon: buildMarkerIcon(markerColor),
        optimized: true,
        zIndex: LIFT_MARKER_Z_INDEX,
      });

      marker.addListener("click", function () {
        infoWindowRef.current.setContent(buildInfoWindowContent(lift));
        infoWindowRef.current.open({ map: map, anchor: marker });
      });

      markersRef.current.push(marker);
    });

    // DC location icon. Added to the bounds so the default view always shows it.
    if (dc) {
      const dcPosition = new google.maps.LatLng(dc.latitude, dc.longitude);
      bounds.extend(dcPosition);

      const dcMarker = new google.maps.Marker({
        position: dcPosition,
        map: map,
        title: dc.name + " (DC)",
        icon: buildDcMarkerIcon(),
        optimized: true,
        zIndex: DC_MARKER_Z_INDEX,
      });

      dcMarker.addListener("click", function () {
        infoWindowRef.current.setContent(
          buildDcInfoWindowContent(dc, countLiftsAtDc(liftsWithCoords, dc.name))
        );
        infoWindowRef.current.open({ map: map, anchor: dcMarker });
      });

      dcMarkerRef.current = dcMarker;

      // DC geofence: the same radius the backend uses for inDcGeofence, so a pin
      // inside the circle is a lift the table reports as "In DC". Not clickable,
      // so it never blocks clicks on the pins inside it.
      if (dc.radiusMeters) {
        const circle = new google.maps.Circle({
          ...DC_GEOFENCE_STYLE,
          map: map,
          center: dcPosition,
          radius: dc.radiusMeters,
          clickable: false,
          zIndex: DC_CIRCLE_Z_INDEX,
        });
        // Fit the whole circle in the default view, not just its center.
        const circleBounds = circle.getBounds && circle.getBounds();
        if (circleBounds) bounds.union(circleBounds);
        dcCircleRef.current = circle;
      }
    }

    const plottedPoints = markersRef.current.length + (dcMarkerRef.current ? 1 : 0);
    // A single point with no circle: center and zoom in. With a circle, fitBounds
    // sizes the view to the geofence instead of a fixed zoom.
    if (plottedPoints === 1 && !dcCircleRef.current) {
      map.setCenter(bounds.getCenter());
      map.setZoom(14);
    } else {
      map.fitBounds(bounds, { top: 40, right: 40, bottom: 40, left: 40 });
    }
  }, [mapInitialised, liftList, dcKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ position: "relative", width: "100%", height: "500px" }}>
      {/* Google Map container — always mounted so the Map instance stays attached */}
      <div ref={mapContainerRef} style={{ width: "100%", height: "100%", borderRadius: "8px" }} />

      {/* Loading overlay — sits on top of the map without unmounting it */}
      {loading && (
        <Box
          sx={{
            position: "absolute", top: 0, left: 0,
            width: "100%", height: "100%",
            display: "flex", justifyContent: "center", alignItems: "center",
            backgroundColor: "rgba(255,255,255,0.75)",
            borderRadius: "8px",
            zIndex: 1,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {/* No-location-data overlay — only show when not loading */}
      {noCoords && !loading && (
        <Box
          sx={{
            position: "absolute", top: 0, left: 0,
            width: "100%", height: "100%",
            display: "flex", justifyContent: "center", alignItems: "center",
            backgroundColor: "rgba(255,255,255,0.85)",
            borderRadius: "8px",
          }}
        >
          <Typography sx={{ fontWeight: 400, fontSize: "16px", color: "#58595B" }}>
            No data available.
          </Typography>
        </Box>
      )}

      {/* Legend */}
      {!noCoords && (
        <Box
          sx={{
            position: "absolute", bottom: "48px", left: "10px",
            backgroundColor: "white", borderRadius: "4px",
            padding: "8px 12px",
            boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
            fontSize: "12px",
            display: "flex", flexDirection: "column", gap: "4px",
          }}
        >
          {/* TE-2011: one entry per pin color. Keep in sync with markerColorFor. */}
          {[
            { key: "in-service", label: LIFT_STATUS_LABEL.IN_SERVICE, color: STATUS_COLORS.IN_SERVICE },
            { key: "inactive", label: "Inactive (> " + STALE_DAYS + " days)", color: STATUS_COLORS.INACTIVE },
            { key: "spare", label: LIFT_STATUS_LABEL.SPARE, color: STATUS_COLORS.SPARE },
          ].map(function (item) {
            return (
              <Box key={item.key} data-testid={"lift-map-legend-" + item.key}
                sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <Box data-testid={"lift-map-legend-swatch-" + item.key} sx={{
                  width: 12, height: 12, borderRadius: "50%",
                  backgroundColor: item.color,
                  border: "2px solid #fff", boxShadow: "0 0 0 1px " + item.color,
                }} />
                <span>{item.label}</span>
              </Box>
            );
          })}
          {/* DC location icon, shown only when the selected DC has coordinates */}
          {dc && (
            <Box data-testid="lift-map-legend-dc"
              sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <img src={buildDcIconUrl(16)} alt="" width={16} height={16}
                style={{ marginLeft: "-2px" }} />
              <span>DC Location</span>
            </Box>
          )}
          {dc && dc.radiusMeters && (
            <Box data-testid="lift-map-legend-dc-geofence"
              sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <Box sx={{
                width: 12, height: 12, borderRadius: "50%",
                border: "2px solid " + DC_GEOFENCE_STYLE.strokeColor,
                backgroundColor: "rgba(32, 39, 165, 0.12)",
              }} />
              <span>DC Geofence</span>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
